'use client'

import { useSearchParams } from 'next/navigation'
import pluralize from 'pluralize'
import { stringifyUrl } from 'query-string'
import { Fragment, ReactElement, cloneElement, useMemo } from 'react'
import styled from 'styled-components'

import { Animate } from '@syconium/little-miss-figgy/dist/components/Animate'
import { Skeleton } from '@syconium/little-miss-figgy/dist/components/Skeleton'
import { TypeStyle } from '@syconium/little-miss-figgy/dist/components/TypeStyle'
import { fromMd } from '@syconium/little-miss-figgy/dist/constants/breakpoints'

import { FloatingFavoritesButton } from '../../../app/_components/buttons/FavoritesButton.client'
import { useNostoClient } from '../../../app/_components/chrome/scripts/NostoScript'
import { NextLink } from '../../../app/_components/navigation/NextLink'
import {
  GutterSizeType,
  PageSection,
  PageSectionContent,
  sectionSideGutterFromMd,
  sectionSideGutterFromMdAndExpanded,
  sectionSideGutterUntilMd,
} from '../../../app/_components/pages/PageSection.client'
import { PricingRange } from '../../../app/_components/pricing/PricingRange.client'
import {
  ProductTileColorCount,
  ProductTileColorName,
  ProductTileColors,
  ProductTileColorsSkeleton,
  ProductTileDetails,
  ProductTileImage,
  ProductTileImageSkeleton,
  ProductTilePrice,
  ProductTilePriceSkeleton,
  ProductTileTitle,
  ProductTileTitleSkeleton,
} from '../../../app/_components/tiles/ProductTile.client'
import {
  LIFTED_BUTTONS_DEFAULT_PIXELS,
  Tile,
  TilesLayout,
} from '../../../app/_components/tiles/Tiles.client'
import { useTrackProductInteraction } from '../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { useNostoRecos } from '../../../lib/hooks/useNostoRecos'
import { NostoPlacementId } from '../../../types/nosto'

const StyledTitle = styled(TypeStyle.HeadlineSmall)`
  margin-bottom: 16px;

  ${fromMd} {
    margin-bottom: 24px;
  }
`

export const RecommendationWrapper = styled.div<{ $withTopBorder?: boolean }>`
  padding: ${o => o.theme.spacing(12, 0)};
  margin-top: ${o => (o.$withTopBorder ? o.theme.spacing(5) : 0)};
  border-top: ${o =>
    o.$withTopBorder
      ? `1px solid ${o.theme.color.stroke.neutral.border.decoration.on.background}`
      : 'none'};
`

export interface NostoProductRecommendationsSectionProps {
  fadeIn?: boolean
  gutterSize?: GutterSizeType
  fullWidth?: boolean
  nostoPlacementId: NostoPlacementId
  /**
   * Optionally override the Nosto-provided recommendation title.
   * Pass a string literal as a simple replacement, or pass a function that is
   * given the Nosto-provided recommendation title and returns a transformed
   * title.
   */
  title?: string | ((nostoTitle: string | undefined) => string | undefined)
  wrapper?: ReactElement
}

export const NostoProductRecommendationsSection = ({
  fadeIn,
  gutterSize = 'default',
  fullWidth = false,
  nostoPlacementId,
  title: titleOverride,
  wrapper,
}: NostoProductRecommendationsSectionProps) => {
  const {
    magnolia: {
      productCarouselColorCount: colorCountText,
      general: { scrollBack: scrollBackLabel, scrollForward: scrollForwardLabel },
    },
  } = useTranslation()
  const { trackProductInteraction } = useTrackProductInteraction()

  const nostoClient = useNostoClient()

  const {
    placementTitle: nostoTitle,
    variants: variantRecs,
    resultId: nostoProductsId,
    status,
  } = useNostoRecos({
    nostoPlacementId,
    productCountLimit: 8,
  })

  // plain=true query param is used by iOS app to view webpages within a webview without the website's header/footer
  const plainQueryParamValue = useSearchParams()?.get('plain') ?? undefined
  const variants = useMemo(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return (variantRecs ?? []).map(variantRec => {
      return {
        ...variantRec,
        urlPath: stringifyUrl({
          url: variantRec.group.urlPath,
          query: { nosto: nostoProductsId, plain: plainQueryParamValue },
        }),
      }
    })
  }, [nostoProductsId, plainQueryParamValue, variantRecs])

  const title = useMemo<string | undefined>(() => {
    return typeof titleOverride === 'function'
      ? titleOverride(nostoTitle)
      : titleOverride ?? nostoTitle
  }, [nostoTitle, titleOverride])

  const tilesLayoutChoices = useMemo(() => {
    return {
      sm: 'slider',
      md: 'slider',
    } as const
  }, [])

  const tilesPerViewportWidth = useMemo(() => {
    return {
      sm: 2,
      md: 4,
    }
  }, [])

  const tilesGutterChoices = useMemo(() => {
    const gutterChoiceSm = gutterSize === 'none' ? 0 : sectionSideGutterUntilMd
    const gutterChoiceMd =
      gutterSize === 'none'
        ? 0
        : gutterSize === 'default'
        ? sectionSideGutterFromMd
        : sectionSideGutterFromMdAndExpanded
    return {
      sm: gutterChoiceSm,
      md: gutterChoiceMd,
    } as const
  }, [gutterSize])

  const skeletonTiles = useMemo(() => [0, 1, 2, 3, 4, 5, 6, 7, 8, 9], [])

  const OptionalFadeIn = fadeIn ? Animate.FadeIn : Fragment

  if (status === 'rejected' || (status === 'resolved' && variants.length === 0)) return null

  return cloneElement(wrapper ?? <></>, {
    children: (
      <PageSection type='NostoProductRecommendationsSection'>
        <PageSectionContent gutterSize={gutterSize}>
          <StyledTitle>
            {status === 'pending' ? <Skeleton animated height='42px' maxWidth='225px' /> : title}
          </StyledTitle>
        </PageSectionContent>
        <PageSectionContent gutterSize='none' fullWidth={fullWidth}>
          <OptionalFadeIn>
            <TilesLayout
              scrollBackLabel={scrollBackLabel}
              scrollForwardLabel={scrollForwardLabel}
              liftButtonsPixels={LIFTED_BUTTONS_DEFAULT_PIXELS}
              layout={tilesLayoutChoices}
              visibleTiles={tilesPerViewportWidth}
              gutters={tilesGutterChoices}
            >
              {status === 'pending'
                ? skeletonTiles.map((_, index) => {
                    return (
                      <Tile key={index}>
                        <ProductTileImageSkeleton aspectRatio={'slider'} />
                        <ProductTileDetails untilMdLeftPadding={false}>
                          <ProductTileTitleSkeleton />
                          <ProductTileColorsSkeleton />
                          <ProductTilePriceSkeleton />
                        </ProductTileDetails>
                      </Tile>
                    )
                  })
                : variants.map((variant, index) => {
                    const imageUrl = variant.group.images?.[0]?.url
                    if (!imageUrl) return null

                    return (
                      <Tile key={`${variant.id}${index}`}>
                        <NextLink
                          href={variant.group.urlPath}
                          onClick={() => {
                            trackProductInteraction({
                              handle: variant.group.product.handle,
                              color: variant.group.color?.id,
                              id: variant.group.id,
                              shopifyId: variant.shopifyProductId,
                              soldOut: variant.group.availability === 'OUT_OF_STOCK',
                            })
                            if (nostoProductsId) {
                              nostoClient.recordProductClick(
                                variant.shopifyProductId,
                                nostoProductsId
                              )
                            }
                          }}
                        >
                          <ProductTileImage
                            alt={variant.group.images?.[0]?.altText || variant.group.title}
                            loading='lazy'
                            src={imageUrl}
                            aspectRatio={'slider'}
                            widths={{
                              unit: 'vw',
                              sm: 100 / tilesPerViewportWidth.sm,
                              md: 100 / tilesPerViewportWidth.md,
                            }}
                          />
                          <ProductTileDetails untilMdLeftPadding={false}>
                            <ProductTileTitle>{variant.group.title}</ProductTileTitle>
                            <ProductTileColors>
                              {variant.group.color?.id !== 'No Color' && (
                                <ProductTileColorName>
                                  {variant.group.color?.displayValue}
                                </ProductTileColorName>
                              )}
                              <ProductTileColorCount>
                                {variant.group.product.options.colorsTotalCount}{' '}
                                {pluralize(
                                  colorCountText,
                                  variant.group.product.options.colorsTotalCount
                                )}
                              </ProductTileColorCount>
                            </ProductTileColors>
                            <ProductTilePrice>
                              <PricingRange {...variant.pricing} />
                            </ProductTilePrice>
                          </ProductTileDetails>
                        </NextLink>
                        {variant.group.color?.id &&
                          variant.fit?.id &&
                          variant.group.product.__typename === 'ProductCatalogStandardProduct' && (
                            <FloatingFavoritesButton
                              productGroupHandle={variant.group.product.handle}
                              colorKey={variant.group.color.id}
                              fitKey={variant.fit.id}
                              variantGroupId={variant.group.id}
                              shopifyProductId={variant.shopifyProductId}
                              productGroupImageSrc={imageUrl}
                            />
                          )}
                      </Tile>
                    )
                  })}
            </TilesLayout>
          </OptionalFadeIn>
        </PageSectionContent>
      </PageSection>
    ),
  })
}
